import { ISourceSelectorState } from "../../../Shared/components/DashboardSourceSelector/DashboardSourceSelector";
import { v4 as uuidv4 } from "uuid";
import {
  IRequestOptions,
  AuthorizationToken,
  postRequest,
  IUserProviderState
} from "../../../Shared/utilities/RequestUtilities";
import { ApiParsingUtilities } from "../../../Shared/utilities/ApiParsingUtilities";
import { ApiConfiguration } from "../../../Shared/utilities/ApiRules";
import { ApiError } from "../../../Shared";

export interface AutomatedDashboardConfiguration extends ApiConfiguration {
  endpoints: {
    s2SGetDashboard: string;
    s2SGetDashboardAI: string;
    s2sRetryAction: string;
    s2SGetJE: string;
    clearTenantDetails?: string;
  };
}

export type statusType =
  | "Draft"
  | "Sent To Poster"
  | "Draft (Clarification needed)"
  | "Draft (Recalled)"
  | "Delete"
  | "Signoff Not Req'd"
  | "Unknown"
  | "Validated"
  | "Submitted"
  | "Failed in SAP"
  | "Voided and Retried";

export const ALL_STATUS: statusType[] = [
  "Draft",
  "Sent To Poster",
  "Draft (Clarification needed)",
  "Draft (Recalled)",
  "Delete",
  "Signoff Not Req'd",
  "Unknown",
  "Validated",
  "Submitted",
  "Failed in SAP",
  "Voided and Retried"
];

export interface AutomatedJeDashboardElement {
  jeId: string;
  jeDocNum: string;
  jeName: string;
  jeStatusId: string;
  jeStatus: string;
  companyCode: string;
  dueDate: string;
  jeReason: string;
  jeType: string;
  header: string;
  sapAmount: string;
  sourceAmount: string;
  differenceAmount: string;
  refguid: string;
  jemUrl: string | null;
  fileName: string;
  submittedTime: string;
  rowWeightage: number;
  jeSAPErrorInfo: JeErrorData | null;
  retryPerformedBy: string;
  retryPerformedDate: string;
  postingType: string;
  showClearButton: boolean;
  currency: string;
  postingMode: string;
  jeRequestor: string;
}
export interface AIAutomatedJeDashboardElement extends AutomatedJeDashboardElement {
  totalCount: number;
  nextPageIndex: number;
  totalPages: number;
  uiLimit: number;
  exportLimit: number;
  searchType: string;
  facets: StatusFacet;
}

export interface DashboardApiPayload {
  jeId: number;
  jeDocNum: string | null | "";
  jeName: string | null | "";
  jeStatusId: number;
  jeStatus: string | "";
  companyCode: string | null | "";
  dueDate: string | null | "";
  jeReason: string | null | "";
  jeType: string | null | "";
  jeHeaderText: string | null | "";
  sapAmount: number | null | 0;
  sourceAmount: number | null | 0;
  differenceAmount: number | null | 0;
  refguid: string | null;
  jemUrl: string | null;
  fileName: string | null;
  submittedTime: string | null | "";
  actionRowWeightage: number | null | 0;
  jeSAPErrorInfo: JeErrorData | null;
  retryPerformedBy: string | null | "";
  retryPerformedDate: string | null | "";
  postingMode: string | null | "";
  postingType: string | null | "";
  showClearButton: boolean | false;
  currency: string | null | "";
  jeRequestor: string | null | "";
}
export interface AIDashboardApiPayload {
  s2SJes: DashboardApiPayload[];
  totalCount: number;
  nextPageIndex: number;
  totalPages: number;
  uiLimit: number;
  exportLimit: number;
  searchType: string;
  facets: StatusFacet;
}
export interface JeErrorData {
  jeHeaderError: string | null | "";
  jeLineItemErrors: LineItemErrorInfo[] | null;
}

export interface LineItemErrorInfo {
  lineNumber: string | null;
  description: string | null;
}

interface IDashboardEndpointPayload {
  appId: string;
  autoApprovedJeCount: number;
  failedJeCount: number;
  s2SJes: DashboardApiPayload[];
}
export interface IDashboardEndpointPayloadAI extends IDashboardEndpointPayload {
  searchType: string;
  uiLimit: number;
  exportLimit: number;
  facets: StatusFacet;
  nextPageIndex: number;
  totalCount: number;
  totalPages: number;
}
export interface StatusFacet {
  statusCode: string;
  count: number;
}

// todo: once we have at least 3 types of request (get post patch delete) for jem convert this into a set of utilities
const getDashboardData = async (
  args: ISourceSelectorState,
  configuration: AutomatedDashboardConfiguration,
  opts?: IRequestOptions<AutomatedJeDashboardElement[]>,
  pageSize?: number,
  nextPageIndex?: number
): Promise<IDashboardEndpointPayload | IDashboardEndpointPayloadAI | undefined> => {
  const headers: Record<string, string> = {
    accept: "application/json",
    requestid: uuidv4()
  };
  if (opts?.getTokenFn && typeof opts.getTokenFn === "function") {
    headers.Authorization = await AuthorizationToken(opts.getTokenFn);
  }
  let s2sEndpoint;
  if (args?.isAISearchEnabled === true) {
    s2sEndpoint = configuration.endpoints.s2SGetDashboardAI
      .replace("{fiscalYear}", `${args.fiscalYear}`)
      .replace("{fiscalPeriod}", `${args.fiscalPeriod}`)
      .replace("{appId}", `${args.appId.toLocaleUpperCase()}`)
      .replace("{UILimit}", `${pageSize || 40}`)
      .replace("{nextPageIndex}", `${nextPageIndex || 0}`);
  } else {
    s2sEndpoint = configuration.endpoints.s2SGetDashboard
      .replace("{fiscalYear}", `${args.fiscalYear}`)
      .replace("{fiscalPeriod}", `${args.fiscalPeriod}`)
      .replace("{appId}", `${args.appId}`);
  }
  const fullEndpoint = configuration.baseApiUrl + s2sEndpoint;
  const resp = await fetch(fullEndpoint, {
    headers,
    referrerPolicy: "strict-origin-when-cross-origin",
    method: "GET",
    mode: "cors"
  });

  const json = await resp.json();

  if (args?.isAISearchEnabled === true) {
    return json as IDashboardEndpointPayloadAI;
  }
  return json as IDashboardEndpointPayload;
};

export function singleS2SJeToElement(s2sJe: DashboardApiPayload): AutomatedJeDashboardElement {
  const newEl: AutomatedJeDashboardElement = {
    jeId: ApiParsingUtilities.addDefault(s2sJe.jeId),
    jeDocNum: ApiParsingUtilities.addDefault(s2sJe.jeDocNum),
    jeName: ApiParsingUtilities.addDefault(s2sJe.jeName),
    jeStatusId: ApiParsingUtilities.addDefault(s2sJe.jeStatusId),
    jeStatus: ApiParsingUtilities.addDefault(s2sJe.jeStatus),
    companyCode: ApiParsingUtilities.addDefault(s2sJe.companyCode),
    jeReason: ApiParsingUtilities.addDefault(s2sJe.jeReason),
    jeType: ApiParsingUtilities.addDefault(s2sJe.jeType),
    header: ApiParsingUtilities.parseJeHeader(s2sJe.jeHeaderText),
    dueDate: ApiParsingUtilities.parseDate(s2sJe.dueDate),
    sapAmount: ApiParsingUtilities.parseNumberWithJeStatus(s2sJe.sapAmount, s2sJe.jeStatus),
    sourceAmount: ApiParsingUtilities.parseNumberWithJeStatus(s2sJe.sourceAmount, s2sJe.jeStatus),
    differenceAmount: ApiParsingUtilities.parseNumberWithJeStatus(s2sJe.differenceAmount, s2sJe.jeStatus),
    refguid: ApiParsingUtilities.addDefault(s2sJe.refguid),
    jemUrl: s2sJe.jemUrl,
    fileName: ApiParsingUtilities.addDefault(s2sJe.fileName),
    // submitted time gets parsed to date with timestamp so it can be sorted
    submittedTime: ApiParsingUtilities.parseDateWithTimeStamp(s2sJe.submittedTime),
    rowWeightage: s2sJe.actionRowWeightage || 0,
    jeSAPErrorInfo: s2sJe.jeSAPErrorInfo,
    retryPerformedBy: ApiParsingUtilities.addDefault(s2sJe.retryPerformedBy),
    retryPerformedDate: ApiParsingUtilities.parseDateWithTimeStamp(s2sJe.retryPerformedDate),
    postingType: ApiParsingUtilities.addDefault(s2sJe.postingType),
    showClearButton: s2sJe.showClearButton,
    currency: ApiParsingUtilities.addDefault(s2sJe.currency), // Assuming currency is same as company code for now
    postingMode: ApiParsingUtilities.addDefault(s2sJe.postingMode),
    jeRequestor: ApiParsingUtilities.addDefault(s2sJe.jeRequestor)
  };
  return newEl;
}
export function AISingleS2SJeToElement(data: AIDashboardApiPayload): AIAutomatedJeDashboardElement[] {
  return data.s2SJes.map((s2sJe: DashboardApiPayload) => {
    const newAIEl: AIAutomatedJeDashboardElement = {
      jeId: ApiParsingUtilities.addDefault(s2sJe.jeId),
      jeDocNum: ApiParsingUtilities.addDefault(s2sJe.jeDocNum),
      jeName: ApiParsingUtilities.addDefault(s2sJe.jeName),
      jeStatusId: ApiParsingUtilities.addDefault(s2sJe.jeStatusId),
      jeStatus: ApiParsingUtilities.addDefault(s2sJe.jeStatus),
      companyCode: ApiParsingUtilities.addDefault(s2sJe.companyCode),
      jeReason: ApiParsingUtilities.addDefault(s2sJe.jeReason),
      jeType: ApiParsingUtilities.addDefault(s2sJe.jeType),
      header: ApiParsingUtilities.parseJeHeader(s2sJe.jeHeaderText),
      dueDate: ApiParsingUtilities.parseDate(s2sJe.dueDate),
      sapAmount: ApiParsingUtilities.parseNumberWithJeStatus(s2sJe.sapAmount, s2sJe.jeStatus),
      sourceAmount: ApiParsingUtilities.parseNumberWithJeStatus(s2sJe.sourceAmount, s2sJe.jeStatus),
      differenceAmount: ApiParsingUtilities.parseNumberWithJeStatus(s2sJe.differenceAmount, s2sJe.jeStatus),
      refguid: ApiParsingUtilities.addDefault(s2sJe.refguid),
      jemUrl: s2sJe.jemUrl,
      fileName: ApiParsingUtilities.addDefault(s2sJe.fileName),
      // submitted time gets parsed to date with timestamp so it can be sorted
      submittedTime: ApiParsingUtilities.parseDateWithTimeStamp(s2sJe.submittedTime),
      rowWeightage: s2sJe.actionRowWeightage || 0,
      jeSAPErrorInfo: s2sJe.jeSAPErrorInfo,
      retryPerformedBy: ApiParsingUtilities.addDefault(s2sJe.retryPerformedBy),
      retryPerformedDate: ApiParsingUtilities.parseDateWithTimeStamp(s2sJe.retryPerformedDate),
      postingType: ApiParsingUtilities.addDefault(s2sJe.postingType),
      showClearButton: s2sJe.showClearButton,
      currency: ApiParsingUtilities.addDefault(s2sJe.currency) || "", // Assuming currency is same as company code for now
      totalCount: data.totalCount || 0,
      nextPageIndex: data.nextPageIndex || 0,
      totalPages: data.totalPages || 0,
      uiLimit: data.uiLimit || 0,
      exportLimit: data.exportLimit || 0,
      searchType: ApiParsingUtilities.addDefault(data.searchType),
      facets: data.facets || {
        statusCode: "",
        count: 0
      },
      postingMode: ApiParsingUtilities.addDefault(s2sJe.postingMode),
      jeRequestor: ApiParsingUtilities.addDefault(s2sJe.jeRequestor)
    };
    return newAIEl;
  });
}

export const getAutomatedJeDashboardData = async (
  args: ISourceSelectorState,
  configuration: AutomatedDashboardConfiguration,
  opts?: IRequestOptions<AutomatedJeDashboardElement[]>,
  pageSize?: number,
  nextPageIndex?: number
): Promise<AutomatedJeDashboardElement[] | AIAutomatedJeDashboardElement[]> => {
  try {
    if (opts?.mockFn) {
      return opts.mockFn(args);
    }
    const apiData = await getDashboardData(args, configuration, opts, pageSize, nextPageIndex);
    if (!apiData?.s2SJes) {
      return [];
    }
    let dataForDashboard;
    if ("facets" in apiData) {
      dataForDashboard = AISingleS2SJeToElement(apiData);
    } else {
      dataForDashboard = apiData.s2SJes.map(singleS2SJeToElement);
    }
    return dataForDashboard;
  } catch (e) {
    console.error(e);
    return [];
  }
};

export const cancelAutomatedJeDashboardItems = async (
  configuration: AutomatedDashboardConfiguration,
  accessToken: IUserProviderState["accessToken"],
  refGuids: string[]
): Promise<any> => {
  const endpoint = `${configuration.baseApiUrl}${configuration.endpoints.clearTenantDetails}`;
  const payload = {
    refGuids
  };
  const response = await postRequest<string[]>(endpoint, payload, accessToken);
  if (!response) {
    throw new ApiError("Error from Server.");
  }
  return response;
};
